import { observer } from 'mobx-react-lite';
import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import Popup from '../../../../components/common-components/pop-up/Popup';
import { useStore } from '../../../../hooks/useStore';
import CommonButton from '../../../../components/common-components/button/CommonButton';
import { FormikTextField } from '../../../../components/form-components/common-components/text-field/FormikTextField';
import { FormikDropdown } from '../../../../components/form-components/common-components/dropdown/FormikDropdown';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import styles from './NewAppointmentPopup.module.css';
import { AppointmentSchema } from '../../../../schemas/AppointmentSchema';
import { Step1HospitalSchema, Step2TagSchema } from '../../../../schemas/AppointmentStepperSchema';
import {
  AppointmentFormValues,
  appointmentInitialValues,
} from '../../../../models/appointment/AppointmentModel';
import { PersonFormValues, personInitialValues } from '../../../../models/person/PersonModel';
import { toast } from 'react-toastify';
import { PersonSchema } from '../../../../schemas/PersonSchema';
import { AppointmentTimeFields } from './AppointmentTimeFields';
import { userManagementService } from '../../../../services/modules/userManagement/userManagement.service';
import { IUserManagementResult } from '../../../../models/user-management/UserManagementModel';

enum AppointmentStep {
  Hospital = 0,
  TagNumber = 1,
  Person = 2,
  Appointment = 3,
}

const STEPS = ['Hastane Oluştur', 'Etiket Numarası', 'Kişi Oluştur', 'Randevu Bilgileri'];

interface Step1FormValues {
  hospitalName: string;
  hospitalLocation: string;
}

const step1InitialValues: Step1FormValues = {
  hospitalName: '',
  hospitalLocation: '',
};

const YEAR_PREFIX = new Date().getFullYear().toString().slice(-2) + '.';

interface Step2FormValues {
  hospitalId: number | null;
  prefix: string;
}

interface Step1Response {
  id: number;
  hospitalName: string;
}

interface Step2Response {
  prefix: string;
}

interface Step3Response {
  id: number;
  name: string;
}

const Step2HospitalIdSetter = ({ hospitalId }: { hospitalId: number }) => {
  const { setFieldValue } = useFormikContext<Step2FormValues>();
  useEffect(() => {
    setFieldValue('hospitalId', hospitalId);
  }, [hospitalId, setFieldValue]);
  return null;
};

const PopupFooter = ({
  onClose,
  activeStep,
  formSubmitButtonRef,
  isLoading,
}: {
  onClose: () => void;
  activeStep: AppointmentStep;
  formSubmitButtonRef: React.MutableRefObject<HTMLButtonElement | null>;
  isLoading: boolean;
}) => {
  const handleSaveClick = () => {
    formSubmitButtonRef.current?.click();
  };

  return (
    <>
      <CommonButton type="cancel" onClick={onClose} disabled={isLoading}>
        İptal
      </CommonButton>
      <CommonButton type="save" onClick={handleSaveClick} disabled={isLoading} loading={isLoading}>
        {activeStep < AppointmentStep.Appointment ? 'Kaydet ve Devam Et' : 'Kaydet'}
      </CommonButton>
    </>
  );
};

const NewAppointmentPopup = () => {
  const { popupStore, hospitalStore, hospitalFilesStore, uiStore, personStore, appointmentStore } =
    useStore();
  const [activeStep, setActiveStep] = useState<AppointmentStep>(AppointmentStep.Hospital);
  const [step1Data, setStep1Data] = useState<Step1Response | null>(null);
  const [step2Data, setStep2Data] = useState<Step2Response | null>(null);
  const [step3Data, setStep3Data] = useState<Step3Response | null>(null);
  const [isStep1Submitting, setIsStep1Submitting] = useState(false);
  const [isStep2Submitting, setIsStep2Submitting] = useState(false);
  const [isStep3Submitting, setIsStep3Submitting] = useState(false);
  const [users, setUsers] = useState<IUserManagementResult[]>([]);
  const formSubmitButtonRef = useRef<HTMLButtonElement | null>(null);

  const resetStepper = useCallback(() => {
    setActiveStep(AppointmentStep.Hospital);
    setStep1Data(null);
    setStep2Data(null);
    setStep3Data(null);
  }, []);

  useEffect(() => {
    if (!popupStore.isNewAppointmentOpen) {
      resetStepper();
      return;
    }
  }, [popupStore.isNewAppointmentOpen, resetStepper]);

  useEffect(() => {
    const fetchHospitals = async () => {
      if (
        !popupStore.isNewAppointmentOpen ||
        (activeStep !== AppointmentStep.TagNumber && activeStep !== AppointmentStep.Person)
      )
        return;
      try {
        uiStore.setLoading(true);
        await hospitalStore.listAllHospitals();
      } catch (err) {
        console.error('Error fetching hospitals:', err);
      } finally {
        uiStore.setLoading(false);
      }
    };
    fetchHospitals();
  }, [popupStore.isNewAppointmentOpen, activeStep, hospitalStore, uiStore]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!popupStore.isNewAppointmentOpen || activeStep !== AppointmentStep.Appointment) return;
      try {
        const usersList = await userManagementService.getAllUsers();
        const actualUsers = Array.isArray(usersList)
          ? usersList
          : ((usersList as { value?: IUserManagementResult[] })?.value ?? []);
        setUsers(actualUsers);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };
    fetchUsers();
  }, [popupStore.isNewAppointmentOpen, activeStep]);

  const handleStep1Submit = async (values: Step1FormValues) => {
    try {
      setIsStep1Submitting(true);
      uiStore.setLoading(true);
      const response = await hospitalStore.newHospital({
        hospitalName: values.hospitalName,
        hospitalLocation: values.hospitalLocation,
        hospitalAddress: values.hospitalLocation,
      });

      if (response.error !== true && response.value) {
        const hospital = response.value as { id: number; hospitalName?: string };
        setStep1Data({
          id: hospital.id,
          hospitalName: hospital.hospitalName || values.hospitalName,
        });
        toast.success('Hastane başarıyla oluşturuldu');
        setActiveStep(AppointmentStep.TagNumber);
      } else {
        toast.error(response.message || 'Hastane oluşturulamadı');
      }
    } catch (error) {
      console.error('Hastane oluşturulurken hata:', error);
    } finally {
      setIsStep1Submitting(false);
      uiStore.setLoading(false);
    }
  };

  const handleStep2Submit = async (values: Step2FormValues) => {
    if (!step1Data?.id || !values.prefix) return;
    try {
      setIsStep2Submitting(true);
      uiStore.setLoading(true);
      const response = await hospitalFilesStore.addHospitalTagPrefix({
        hospitalId: step1Data.id,
        prefix: values.prefix,
      });

      if (response.error !== true) {
        setStep2Data({
          prefix: values.prefix,
        });
        toast.success('Etiket ön eki başarıyla oluşturuldu');
        setActiveStep(AppointmentStep.Person);
      } else {
        toast.error(response.message || 'Etiket ön eki oluşturulamadı');
      }
    } catch (error) {
      console.error('Etiket ön eki oluşturulurken hata:', error);
    } finally {
      setIsStep2Submitting(false);
      uiStore.setLoading(false);
    }
  };

  const handleStep3Submit = async (values: PersonFormValues) => {
    if (!step1Data?.id) return;
    try {
      setIsStep3Submitting(true);
      uiStore.setLoading(true);
      const response = await personStore.createPerson({
        ...values,
        hospitalId: step1Data.id,
      });

      if (response.error !== true && response.value) {
        setStep3Data({
          id: response.value.id!,
          name: response.value.name || '',
        });
        toast.success('Kişi başarıyla oluşturuldu');
        setActiveStep(AppointmentStep.Appointment);
      }
    } catch (error) {
      console.error('Kişi oluşturulurken hata:', error);
    } finally {
      setIsStep3Submitting(false);
      uiStore.setLoading(false);
    }
  };

  const handleAppointmentSubmit = async (values: AppointmentFormValues) => {
    try {
      if (!step1Data?.id || !step3Data?.id) {
        toast.error('Lütfen önce hastane ve kişi oluşturun');
        return;
      }

      const response = await appointmentStore.createAppointment({
        ...values,
        hospitalId: step1Data.id,
        personId: step3Data.id,
        requestNumber: step2Data?.prefix || undefined,
      });

      if (response.error !== true) {
        popupStore.setIsNewAppointmentOpen(false);
        resetStepper();
      }
    } catch (error) {
      console.error('Randevu oluşturulurken hata:', error);
    }
  };

  const handleClose = () => {
    popupStore.setIsNewAppointmentOpen(false);
    resetStepper();
    toast.dismiss();
  };

  const hospitalOptionsForStep2 = useMemo(
    (): Array<{ value: number; label: string }> =>
      hospitalStore.hospitalList
        .filter((h): h is { id: number; hospitalName?: string } => h.id !== undefined)
        .map(h => ({ value: h.id, label: h.hospitalName || 'Hastane' })),
    [hospitalStore.hospitalList]
  );

  const hospitalOptionsForStep3 = useMemo(
    (): Array<{ value: number; label: string }> =>
      hospitalStore.hospitalList
        .filter((h): h is { id: number; hospitalName?: string } => h.id !== undefined)
        .map(h => ({ value: h.id, label: h.hospitalName || 'Hastane' })),
    [hospitalStore.hospitalList]
  );

  const tagOptionsForStep4 = useMemo(() => {
    if (!step2Data?.prefix) return [];
    return [{ value: step2Data.prefix, label: step2Data.prefix }];
  }, [step2Data]);

  const personOptionsForStep4 = useMemo(() => {
    if (!step3Data) return [];
    return [{ value: step3Data.id, label: step3Data.name }];
  }, [step3Data]);

  const userOptionsForStep4 = useMemo(
    (): Array<{ value: string; label: string }> =>
      users
        .filter(u => u.id && !u.isDeleted)
        .map(u => ({
          value: u.id,
          label: `${u.userName}${u.firstName && u.lastName ? ` (${u.firstName} ${u.lastName})` : ''}`,
        })),
    [users]
  );

  const step2InitialValues = useMemo(
    (): Step2FormValues => ({
      hospitalId: step1Data?.id ?? null,
      prefix: YEAR_PREFIX,
    }),
    [step1Data]
  );

  const appointmentInitialValuesWithStepData = useMemo(
    () => ({
      ...appointmentInitialValues,
      hospitalId: step1Data?.id ?? null,
      personId: step3Data?.id ?? null,
      requestNumber: step2Data?.prefix ?? '',
    }),
    [step1Data, step2Data, step3Data]
  );

  const personInitialValuesWithHospital = useMemo(
    () => ({
      ...personInitialValues,
      hospitalId: step1Data?.id ?? null,
    }),
    [step1Data]
  );

  const isLoading =
    uiStore.isLoading || isStep1Submitting || isStep2Submitting || isStep3Submitting;

  return (
    <Popup
      isOpen={popupStore.isNewAppointmentOpen}
      onClose={handleClose}
      title="Yeni Randevu Oluştur"
      footer={() => (
        <PopupFooter
          onClose={handleClose}
          activeStep={activeStep}
          formSubmitButtonRef={formSubmitButtonRef}
          isLoading={isLoading}
        />
      )}
    >
      <div className={styles.container}>
        <Stepper activeStep={activeStep} alternativeLabel className={styles.stepper}>
          {STEPS.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === AppointmentStep.Hospital && (
          <Formik
            initialValues={step1InitialValues}
            validationSchema={Step1HospitalSchema}
            onSubmit={handleStep1Submit}
          >
            {() => (
              <>
                <Form className={styles.form}>
                  <button
                    ref={formSubmitButtonRef}
                    type="submit"
                    style={{ display: 'none' }}
                    tabIndex={-1}
                    aria-hidden
                  />
                  <div className={styles.stepContent}>
                    {uiStore.isLoading && (
                      <div className={styles.loadingOverlay}>
                        <div className={styles.spinner} />
                        <span>Yükleniyor...</span>
                      </div>
                    )}
                    <div className={styles.inputContainer}>
                      <FormikTextField
                        name="hospitalName"
                        label="Hastane Adı"
                        placeholder="Hastane adını giriniz"
                        required
                      />
                    </div>
                    <div className={styles.inputContainer}>
                      <FormikTextField
                        name="hospitalLocation"
                        label="Hastane Lokasyonu"
                        placeholder="Hastane lokasyonunu giriniz"
                        required
                      />
                    </div>
                  </div>
                </Form>
              </>
            )}
          </Formik>
        )}

        {activeStep === AppointmentStep.TagNumber && step1Data && (
          <Formik
            initialValues={step2InitialValues}
            validationSchema={Step2TagSchema}
            onSubmit={handleStep2Submit}
            enableReinitialize
          >
            {() => (
              <>
                <Step2HospitalIdSetter hospitalId={step1Data.id} />
                <Form className={styles.form}>
                  <button
                    ref={formSubmitButtonRef}
                    type="submit"
                    style={{ display: 'none' }}
                    tabIndex={-1}
                    aria-hidden
                  />
                  <div className={styles.stepContent}>
                    {uiStore.isLoading && (
                      <div className={styles.loadingOverlay}>
                        <div className={styles.spinner} />
                        <span>Yükleniyor...</span>
                      </div>
                    )}
                    <div className={styles.inputContainer}>
                      <FormikDropdown
                        name="hospitalId"
                        options={hospitalOptionsForStep2}
                        label="Hastane"
                        disabled
                      />
                    </div>
                    <div className={styles.inputContainer}>
                      <FormikTextField
                        name="prefix"
                        label="Ön Ek"
                        placeholder="Ön ek giriniz (örn: ETK)"
                        required
                      />
                    </div>
                  </div>
                </Form>
              </>
            )}
          </Formik>
        )}

        {activeStep === AppointmentStep.Person && (
          <Formik
            initialValues={personInitialValuesWithHospital}
            validationSchema={PersonSchema}
            onSubmit={handleStep3Submit}
          >
            {() => (
              <>
                <Form className={styles.form}>
                  <button
                    ref={formSubmitButtonRef}
                    type="submit"
                    style={{ display: 'none' }}
                    tabIndex={-1}
                    aria-hidden
                  />
                  <div className={styles.stepContent}>
                    {uiStore.isLoading && (
                      <div className={styles.loadingOverlay}>
                        <div className={styles.spinner} />
                        <span>Yükleniyor...</span>
                      </div>
                    )}
                    <div className={styles.inputContainer}>
                      <FormikDropdown
                        name="hospitalId"
                        options={hospitalOptionsForStep3}
                        label="Hastane"
                        disabled
                      />
                    </div>
                    <div className={styles.inputContainer}>
                      <FormikTextField
                        name="name"
                        label="İsim"
                        placeholder="İsim giriniz"
                        required
                      />
                    </div>
                    <div className={styles.inputContainer}>
                      <FormikTextField
                        name="phoneNumber"
                        label="Telefon Numarası"
                        placeholder="(5XX) XXX XX XX"
                        type="tel"
                        mask={(value: string) => {
                          const numbers = value.replace(/\D/g, '');
                          if (numbers.length <= 3) {
                            return numbers.length > 0 ? `(${numbers}` : '';
                          } else if (numbers.length <= 6) {
                            return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
                          } else if (numbers.length <= 8) {
                            return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)} ${numbers.slice(6)}`;
                          } else {
                            return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)} ${numbers.slice(
                              6,
                              8
                            )} ${numbers.slice(8, 10)}`;
                          }
                        }}
                      />
                    </div>
                    <div className={styles.inputContainer}>
                      <FormikTextField
                        name="email"
                        label="E-mail"
                        placeholder="E-mail giriniz (opsiyonel)"
                        type="email"
                      />
                    </div>
                  </div>
                </Form>
              </>
            )}
          </Formik>
        )}

        {activeStep === AppointmentStep.Appointment && (
          <Formik
            initialValues={appointmentInitialValuesWithStepData}
            validationSchema={AppointmentSchema}
            onSubmit={handleAppointmentSubmit}
          >
            {() => (
              <>
                <Form className={styles.form}>
                  <button
                    ref={formSubmitButtonRef}
                    type="submit"
                    style={{ display: 'none' }}
                    tabIndex={-1}
                    aria-hidden
                  />
                  <div className={styles.stepContent}>
                    {uiStore.isLoading && (
                      <div className={styles.loadingOverlay}>
                        <div className={styles.spinner} />
                        <span>Yükleniyor...</span>
                      </div>
                    )}
                    <div className={styles.inputContainer}>
                      <FormikDropdown
                        name="requestNumber"
                        options={tagOptionsForStep4}
                        label="Etiket Numarası"
                        disabled
                      />
                    </div>
                    <div className={styles.inputContainer}>
                      <FormikDropdown
                        name="personId"
                        options={personOptionsForStep4}
                        label="Kişi"
                        disabled
                      />
                    </div>
                    <div className={styles.inputContainer}>
                      <FormikDropdown
                        name="userId"
                        options={userOptionsForStep4}
                        label="Kullanıcı"
                        placeholder="Kullanıcı seçiniz"
                      />
                    </div>
                    <div className={styles.inputContainer}>
                      <FormikTextField
                        name="appointmentDate"
                        label="Randevu Tarihi"
                        type="date"
                        placeholder="Tarih seçiniz"
                        required
                      />
                    </div>
                    <AppointmentTimeFields />
                    <div className={styles.inputContainer}>
                      <FormikTextField
                        name="deviceList"
                        label="Cihaz Listesi"
                        placeholder="Cihaz listesini giriniz"
                        multiline
                        rows={4}
                        required
                      />
                    </div>
                    <div className={styles.inputContainer}>
                      <FormikTextField
                        name="notes"
                        label="Notlar"
                        placeholder="Notları giriniz (opsiyonel)"
                        multiline
                        rows={2}
                      />
                    </div>
                  </div>
                </Form>
              </>
            )}
          </Formik>
        )}
      </div>
    </Popup>
  );
};

export default observer(NewAppointmentPopup);
